import {
  ProvidersTemplate,
  type ProvidersTemplateRow,
} from '../../templates/ProvidersTemplate';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import {
  buildSingleItemPdfEntry,
  isPdfSignedUrlCandidate,
} from './pdfOverride';

type PatientProvidersKey = Extract<
  DocumentKey,
  {
    source: DocumentSource.PROVIDERS;
    type: DocumentType.PATIENT_PROVIDERS;
  }
>;

export interface PatientProviderSourceDocument {
  referenceType?: string;
  contentType?: string;
  signedUrl: string;
}

export interface PatientProviderDetail {
  id: string;
  name?: string;
  specialty?: string;
  telephones?: string[];
  city?: string;
  state?: string;
  street1?: string;
  street2?: string;
  postalCode?: string;
  phoneNumber?: string;
  faxNumber?: string;
  email?: string;
  npi?: string;
  providerId?: string;
  dateUploaded?: string;
  sourceDocuments?: PatientProviderSourceDocument[];
}

export interface PatientProvidersQueryData {
  providers: PatientProviderDetail[];
}

const buildProviderAddress = (
  provider: PatientProviderDetail,
): string | undefined => {
  const street = [provider.street1, provider.street2].filter(Boolean).join(' ');
  const cityState = [provider.city, provider.state].filter(Boolean).join(', ');
  const parts = [street, cityState, provider.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(' • ') : undefined;
};

const buildProviderDisplayName = (
  provider: PatientProviderDetail,
): string | undefined => provider.name?.trim() || undefined;

const toProvidersTemplateRow = (
  provider: PatientProviderDetail,
): ProvidersTemplateRow => ({
  id: provider.id,
  name: buildProviderDisplayName(provider),
  npi: provider.npi,
  providerId: provider.providerId,
  specialty: provider.specialty,
  address: buildProviderAddress(provider),
  phoneNumber: provider.phoneNumber,
  faxNumber: provider.faxNumber,
  email: provider.email,
  telephones: provider.telephones,
});

const getFirstPdfFromSourceDocuments = (
  sourceDocuments: PatientProviderSourceDocument[],
  title?: string,
): DocumentEntry | undefined => {
  for (const doc of sourceDocuments) {
    if (!isPdfSignedUrlCandidate(doc)) continue;
    const signedUrl = doc.signedUrl?.trim();
    if (!signedUrl) continue;
    return {
      kind: 'pdf',
      signedUrl,
      title: doc.referenceType ?? title,
    };
  }
  return undefined;
};

export const adaptPatientProviders = (
  data: PatientProvidersQueryData,
  key: PatientProvidersKey,
): DocumentEntry | undefined => {
  const providers = data?.providers ?? [];
  if (providers.length === 0) return undefined;

  const provider = providers.find((p) => p.id === key.providerId);
  if (!provider) return undefined;

  const title = key.name ?? buildProviderDisplayName(provider);
  const sourceDocuments = provider.sourceDocuments ?? [];

  const singlePdfEntry = buildSingleItemPdfEntry(
    [provider],
    () => sourceDocuments,
    { title },
  );
  if (singlePdfEntry) return singlePdfEntry;

  const firstPdfEntry = getFirstPdfFromSourceDocuments(sourceDocuments, title);
  if (firstPdfEntry) return firstPdfEntry;

  const rows = providers.map(toProvidersTemplateRow);

  return {
    kind: 'template',
    title,
    uploadedAt: provider.dateUploaded,
    render: (ctx) => (
      <ProvidersTemplate
        rows={rows}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
      />
    ),
  };
};
